import { useState, useEffect } from 'react';

import { TGame } from 'types';

const REFRESH_RATE_MS = 5000;

function useGameSelection(games: TGame[], isAuto: boolean) {
  const [pauseAutoSelection, setPauseAutoSelection] = useState<boolean>(false);
  const [selectedGame, setSelectedGame] = useState<TGame | undefined>(isAuto ? games[1] : undefined);

  useEffect(() => {
    let interval: NodeJS.Timeout | undefined;

    if (isAuto) {
      interval = setInterval(() => {
        if (games.length < 2 || pauseAutoSelection) {
          return;
        }

        const selectedGameIndex = games.findIndex(({ id }) => selectedGame?.id === id);

        if (selectedGameIndex >= 0) {
          if (selectedGameIndex === games.length - 1) {
            // Start from the beginning after the last game in the section.
            setSelectedGame(games[1]);
          } else {
            // We should skip games which are viewed as main items, each 8th game is main item => therefore % 7.
            const nextGame = (selectedGameIndex + 1) % 7 === 0 ? selectedGameIndex + 2 : selectedGameIndex + 1;

            setSelectedGame(nextGame > games.length - 1 ? games[1] : games[nextGame]);
          }
        } else {
          // Start auto game selection from the first game.
          setSelectedGame(games[1]);
        }
      }, REFRESH_RATE_MS);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isAuto, games, selectedGame, pauseAutoSelection]);

  useEffect(() => {
    const handleScroll = () => {
      // If an user has already clicked on the specific game, then we restart auto selection on first scroll.
      if (pauseAutoSelection) {
        setPauseAutoSelection(false);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, [pauseAutoSelection]);

  return {
    selectedGame,
    select: setSelectedGame,
    pause: setPauseAutoSelection,
  };
}

export default useGameSelection;
